// Pure helpers voor de Inkoop-module (F9).
//
// Géén DB-calls, géén Fastify-dependencies, géén `node:*` imports.
// Pure rekenfuncties voor inkoop-totalen, BTW, en sequentie-codes die zowel
// in de API als in de backoffice-UI worden gebruikt.

import type { InkoopMatchStatusValue } from './inkoop-match'
// batch-66 inkoop-afwijkingen (FO-490) — de blokkade-tekst van het afwijkingen-register.
import { afwijkingBetaalBlokkadeTekst, afwijkingVervangenBlokkadeTekst } from './inkoop-afwijkingen'

// ────────────────────────────────────────────────────────────────────────────
// Status-types
// ────────────────────────────────────────────────────────────────────────────

export type InkoopOrderStatusValue =
  | 'CONCEPT'
  | 'GOEDGEKEURD'
  | 'BESTELD'
  | 'ONTVANGEN'
  | 'GEFACTUREERD'
  | 'GEANNULEERD'
  // Besteltraject-uitbreiding (batch-4 inkoop) — alleen toegevoegd, nooit
  // bestaande waarden verwijderd. De bestaande happy-path
  // (CONCEPT -> GOEDGEKEURD -> BESTELD -> ONTVANGEN -> GEFACTUREERD) blijft geldig.
  | 'WACHT_OP_GOEDKEURING'
  | 'GOEDGEKEURD_VOOR_BESTELLING'
  | 'IN_BESTELLING'
  | 'DEELLEVERING_ONTVANGEN'
  | 'VOLLEDIG_ONTVANGEN'
  | 'BINNENGEBOEKT'
  | 'AFGEROND'
  // batch-65 voorraad-besteladvies (FO-481) — automatische order waarvan het
  // bestelbericht niet verstuurd kon worden (mail mislukt).
  | 'TE_VERSTUREN'

// FO-080 (batch-20) — expliciete administratie-statussen (OPEN -> ONTVANGEN,
// GEBOEKT -> GOEDGEKEURD; IN_BEHANDELING nieuw). De werk-statussen wisselen vrij;
// BETAALD/GEANNULEERD blijven terminaal.
// batch-59 interne-facturen (FO-432): KLAARGEZET = een door een herhaal-regel
// klaargezet intern concept dat nog op bevestiging wacht.
export type InkoopFactuurStatusValue =
  | 'KLAARGEZET'
  | 'ONTVANGEN'
  | 'IN_BEHANDELING'
  | 'GOEDGEKEURD'
  | 'BETAALD'
  | 'BETWIST'
  | 'GEANNULEERD'
  // batch-65 factuur-richting (FO-486) — als verkoopconcept doorgezet.
  | 'VERPLAATST_NAAR_VERKOOP'

// ────────────────────────────────────────────────────────────────────────────
// State-machine helpers — orders
// ────────────────────────────────────────────────────────────────────────────

export const INKOOP_ORDER_TRANSITIONS: Record<InkoopOrderStatusValue, InkoopOrderStatusValue[]> = {
  // Bestaande overgangen blijven exact gelijk (legacy/simpele flow + de
  // bestel-low-stock-route gebruiken deze). De besteltraject-flow voegt alleen
  // extra doelen toe; CONCEPT -> BESTELD blijft bewust verboden (goedkeuring
  // niet overslaan) en ONTVANGEN -> GOEDGEKEURD blijft verboden.
  CONCEPT: ['GOEDGEKEURD', 'WACHT_OP_GOEDKEURING', 'GEANNULEERD'],
  GOEDGEKEURD: ['BESTELD', 'GOEDGEKEURD_VOOR_BESTELLING', 'IN_BESTELLING', 'GEANNULEERD'],
  BESTELD: ['ONTVANGEN', 'DEELLEVERING_ONTVANGEN', 'VOLLEDIG_ONTVANGEN', 'GEANNULEERD'],
  ONTVANGEN: ['GEFACTUREERD', 'BINNENGEBOEKT', 'GEANNULEERD'],
  GEFACTUREERD: [], // terminal — daarna leeft het via InkoopFactuur-statussen
  GEANNULEERD: [], // terminal
  // Besteltraject-flow.
  WACHT_OP_GOEDKEURING: ['GOEDGEKEURD', 'GOEDGEKEURD_VOOR_BESTELLING', 'GEANNULEERD'],
  GOEDGEKEURD_VOOR_BESTELLING: ['IN_BESTELLING', 'BESTELD', 'GEANNULEERD'],
  // batch-65 (FO-481): IN_BESTELLING -> TE_VERSTUREN zodra het vangnet een
  // mislukt bestelbericht ontdekt (de mail faalde pas in de verzendwachtrij).
  IN_BESTELLING: ['DEELLEVERING_ONTVANGEN', 'VOLLEDIG_ONTVANGEN', 'ONTVANGEN', 'GEANNULEERD', 'TE_VERSTUREN'],
  DEELLEVERING_ONTVANGEN: ['DEELLEVERING_ONTVANGEN', 'VOLLEDIG_ONTVANGEN', 'GEANNULEERD'],
  VOLLEDIG_ONTVANGEN: ['BINNENGEBOEKT', 'GEFACTUREERD', 'GEANNULEERD'],
  BINNENGEBOEKT: ['AFGEROND', 'GEFACTUREERD'],
  AFGEROND: [], // terminal
  // batch-65 voorraad-besteladvies (FO-481) — na (her)verzenden of "handmatig
  // verstuurd" terug naar IN_BESTELLING; de goederen kunnen ook gewoon binnenkomen
  // (telefonisch besteld) en de order kan geannuleerd worden.
  TE_VERSTUREN: ['IN_BESTELLING', 'DEELLEVERING_ONTVANGEN', 'VOLLEDIG_ONTVANGEN', 'GEANNULEERD'],
}

export function isOrderTransitionAllowed(
  from: InkoopOrderStatusValue,
  to: InkoopOrderStatusValue,
): boolean {
  return INKOOP_ORDER_TRANSITIONS[from].includes(to)
}

export function isOrderTerminalStatus(status: InkoopOrderStatusValue): boolean {
  return INKOOP_ORDER_TRANSITIONS[status].length === 0
}

// ────────────────────────────────────────────────────────────────────────────
// State-machine helpers — facturen
// ────────────────────────────────────────────────────────────────────────────

// FO-080 (batch-20) — versoepelde state-machine: de backoffice mag de
// werk-statussen (ONTVANGEN/IN_BEHANDELING/GOEDGEKEURD/BETWIST) zonder
// goedkeurketen onderling vrij wisselen, plus elk naar BETAALD (vereist
// betaaldatum) of GEANNULEERD. BETAALD en GEANNULEERD zijn terminaal: daar weer
// uit moet expliciet (correctie), niet via de gewone status-zet-actie.
export const INKOOP_FACTUUR_TRANSITIONS: Record<InkoopFactuurStatusValue, InkoopFactuurStatusValue[]> = {
  // batch-59 interne-facturen (FO-432) - een door de herhaal-regel klaargezet
  // intern concept. Alleen de generator zet deze status (hij staat niet in
  // INKOOP_FACTUUR_WERK_STATUSSEN, dus de status-actie kan er nooit naartoe).
  // Eruit kan het via bevestigen (GOEDGEKEURD/BETAALD) of afkeuren
  // (GEANNULEERD); ONTVANGEN blijft mogelijk voor een handmatige degradatie
  // wanneer de reviewer het concept toch als gewone factuur wil behandelen.
  KLAARGEZET: ['ONTVANGEN', 'IN_BEHANDELING', 'GOEDGEKEURD', 'BETAALD', 'GEANNULEERD'],
  // batch-66 inkoop-afwijkingen (FO-490 §2.3): BETWIST is geen doel meer.
  // "Betwist" volgt uit het afwijkingen-register; de status bestaat nog voor
  // historische rijen, die er via de overgangen hieronder weer uit kunnen.
  ONTVANGEN: ['IN_BEHANDELING', 'GOEDGEKEURD', 'BETAALD', 'GEANNULEERD'],
  IN_BEHANDELING: ['ONTVANGEN', 'GOEDGEKEURD', 'BETAALD', 'GEANNULEERD'],
  GOEDGEKEURD: ['ONTVANGEN', 'IN_BEHANDELING', 'BETAALD', 'GEANNULEERD'],
  BETWIST: ['ONTVANGEN', 'IN_BEHANDELING', 'GOEDGEKEURD', 'BETAALD', 'GEANNULEERD'],
  BETAALD: [], // terminal
  GEANNULEERD: [], // terminal
  // batch-65 factuur-richting (FO-486) — terminal voor de gewone status-actie:
  // alleen de verplaats-actie zelf zet 'm (en zet 'm terug als dezelfde PDF
  // alsnog weer als inkoopfactuur geboekt wordt).
  VERPLAATST_NAAR_VERKOOP: [],
}

export function isFactuurTransitionAllowed(
  from: InkoopFactuurStatusValue,
  to: InkoopFactuurStatusValue,
): boolean {
  return INKOOP_FACTUUR_TRANSITIONS[from].includes(to)
}

export function isFactuurTerminalStatus(status: InkoopFactuurStatusValue): boolean {
  return INKOOP_FACTUUR_TRANSITIONS[status].length === 0
}

// ────────────────────────────────────────────────────────────────────────────
// Bedrag-berekeningen — pure rekenfuncties (BTW + totaal)
// ────────────────────────────────────────────────────────────────────────────

export interface InkoopOrderRegelInput {
  aantal: number
  prijs: number
  /** BTW-percentage per regel; null/undefined → gebruik order-default */
  btwPercentage?: number | null
}

/**
 * Subtotaal van één regel = aantal × prijs, afgerond op 2 decimalen.
 */
export function regelSubtotaal(aantal: number, prijs: number): number {
  return roundTwo(aantal * prijs)
}

/**
 * Totalen-berekening voor een hele inkooporder.
 *
 * - subtotaal = som van regel-subtotalen
 * - btwBedrag = som per regel van (regel-subtotaal × regel-btwPct of order-default)
 * - totaal    = subtotaal + btwBedrag
 *
 * Pure functie — geen side-effects. BTW per regel mag verschillen (bv. 9% op
 * boeken, 21% op materiaal); fallback op order-default-percentage.
 */
export interface InkoopOrderTotalen {
  subtotaal: number
  btwBedrag: number
  totaal: number
}

export function berekenInkoopOrderTotalen(
  regels: InkoopOrderRegelInput[],
  btwPercentageDefault: number,
): InkoopOrderTotalen {
  let subtotaal = 0
  let btwBedrag = 0
  for (const r of regels) {
    const sub = regelSubtotaal(r.aantal, r.prijs)
    subtotaal += sub
    const pct =
      r.btwPercentage !== undefined && r.btwPercentage !== null
        ? r.btwPercentage
        : btwPercentageDefault
    btwBedrag += sub * (pct / 100)
  }
  subtotaal = roundTwo(subtotaal)
  btwBedrag = roundTwo(btwBedrag)
  const totaal = roundTwo(subtotaal + btwBedrag)
  return { subtotaal, btwBedrag, totaal }
}

/**
 * Factuur-totalen-berekening (gebruiker geeft handmatig bedrag + btw op).
 * Pure functie — totaal = bedrag + btwBedrag. Beide afgerond op 2 decimalen.
 */
export function berekenInkoopFactuurTotaal(bedrag: number, btwBedrag: number): number {
  return roundTwo(bedrag + btwBedrag)
}

/**
 * BTW uit bedrag berekenen (handig wanneer alleen bruto bekend is).
 * Returnt het BTW-deel van een bruto-bedrag bij gegeven percentage.
 *
 * Voorbeeld: brutoBtw(121, 21) → 21 (van 100 euro netto + 21 euro btw).
 */
export function brutoNaarBtw(bruto: number, btwPercentage: number): number {
  if (btwPercentage <= 0) return 0
  const factor = btwPercentage / 100
  const netto = bruto / (1 + factor)
  return roundTwo(bruto - netto)
}

// ────────────────────────────────────────────────────────────────────────────
// FO-055 (batch-17) P3 — btw incl/excl, beide richtingen
// ────────────────────────────────────────────────────────────────────────────
//
// Bij het inboeken van een inkoopfactuur kun je het bedrag incl. ÓF excl. btw
// invullen + een btw-%; het systeem rekent de andere twee uit. Pure functies,
// rond op het laatste moment (cent-precisie). `btwPercentage` is een percentage
// (21 = 21%), niet een fractie. Per constructie geldt altijd `excl + btw === incl`
// (de afgeleide-richting rondt het btw-deel als verschil), zodat de boek-
// preconditie (`bevestigBlokkades`) niet op afrondingsruis struikelt.

export interface BtwSplitsing {
  /** Bedrag exclusief btw. */
  excl: number
  /** Het btw-bedrag. */
  btw: number
  /** Bedrag inclusief btw (= excl + btw). */
  incl: number
}

/**
 * Splits vanaf het EXCL.-bedrag (excl. is leidend):
 *   btw  = round2(excl × pct/100)
 *   incl = round2(excl + btw)
 */
export function berekenBtwVanafExcl(excl: number, btwPercentage: number): BtwSplitsing {
  const exclR = roundTwo(excl)
  const btw = roundTwo(exclR * (btwPercentage / 100))
  const incl = roundTwo(exclR + btw)
  return { excl: exclR, btw, incl }
}

/**
 * Splits vanaf het INCL.-bedrag (incl. is leidend):
 *   excl = round2(incl / (1 + pct/100))
 *   btw  = round2(incl − excl)
 * Een niet-positieve factor (pct ≤ -100) levert geen deling door ~0 op: dan is
 * er geen btw te splitsen en blijft het bedrag ongewijzigd.
 */
export function berekenBtwVanafIncl(incl: number, btwPercentage: number): BtwSplitsing {
  const inclR = roundTwo(incl)
  const factor = 1 + btwPercentage / 100
  if (factor <= 0) return { excl: inclR, btw: 0, incl: inclR }
  const excl = roundTwo(inclR / factor)
  const btw = roundTwo(inclR - excl)
  return { excl, btw, incl: inclR }
}

/**
 * Leid het btw-% af uit een bestaand excl.- + btw-bedrag (voor de begin-waarde
 * van het boekformulier wanneer er nog geen los percentage is opgeslagen). 0 als
 * het excl.-bedrag 0 is (geen deling door 0).
 */
export function afgeleidBtwPercentage(excl: number, btw: number): number {
  if (excl === 0) return 0
  return roundTwo((btw / excl) * 100)
}

// ────────────────────────────────────────────────────────────────────────────
// Bevestigen & boeken — preconditie-validatie (mailbox-scan review-flow)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Reken een bedrag (euro, max 2 decimalen) om naar hele centen als integer,
 * zonder float-afronding-drift. We schalen met 100 en ronden naar de
 * dichtstbijzijnde cent zodat bv. 1210.00 → 121000 en 12.1 → 1210.
 */
export function naarCenten(bedrag: number): number {
  return Math.round(bedrag * 100)
}

/** Input voor de bevestig-preconditie-check. Pure waarden, geen Decimal/DB. */
export interface BevestigInput {
  /** Of er een leverancier gekoppeld is (Client type=LEVERANCIER). */
  heeftLeverancier: boolean
  /** Of er een BV/entiteit gekoppeld is. */
  heeftEntity: boolean
  bedrag: number
  btwBedrag: number
  totaal: number
  /**
   * batch-59 interne-facturen (FO-432 §6.2) - de factuur staat als interne
   * stroom van een BV aan ZICHZELF (afzender-BV == ontvangende BV). Dat kan
   * niet bestaan; boeken wordt geweigerd met uitleg i.p.v. stil door te laten.
   * Optioneel: bestaande callers zonder intercompany-context blijven gelijk.
   */
  zelfdeBvAfzenderOntvanger?: boolean
}

/**
 * Bepaalt de blokkades die "Bevestigen & boeken" van een te-controleren
 * inkoopfactuur tegenhouden. Pure functie (geen DB/Decimal): de route haalt de
 * waarden op, deze functie beslist. Een lege array betekent "mag geboekt
 * worden".
 *
 * Regels (spec Deel 4):
 *   1. Leverancier moet gekozen zijn.
 *   2. BV (entiteit) moet gekozen zijn.
 *   3. `bedrag + btwBedrag == totaal` met 1-cent tolerantie (cent-int-vergelijk,
 *      geen float-gelijkheid).
 *
 * De teksten zijn mens-leesbaar zodat de UI ze direct kan tonen ("wat moet ik
 * nog corrigeren"). Geen em-dash; merknaam niet relevant hier.
 */
export function bevestigBlokkades(input: BevestigInput): string[] {
  const blokkades: string[] = []
  if (!input.heeftLeverancier) {
    blokkades.push('Kies een leverancier voordat je de factuur boekt.')
  }
  if (!input.heeftEntity) {
    blokkades.push('Kies de BV (entiteit) waaronder deze factuur valt.')
  }
  const verschil = Math.abs(naarCenten(input.bedrag) + naarCenten(input.btwBedrag) - naarCenten(input.totaal))
  if (verschil > 1) {
    blokkades.push('Bedrag plus btw komt niet overeen met het totaal - controleer de bedragen.')
  }
  if (input.zelfdeBvAfzenderOntvanger) {
    blokkades.push(
      'Verzendende en ontvangende BV zijn dezelfde: een BV kan zichzelf niet factureren. ' +
        "Corrigeer de leverancier of de ontvangende BV, of haal de markering \"factuur tussen eigen BV's\" weg.",
    )
  }
  return blokkades
}

/**
 * FO-393 (batch-46) — betaalbaarheids-gate voor een inkoopfactuur. Zusje van
 * `bevestigBlokkades`, op ÉÉN plek aangeroepen door de betaallijst-create, de
 * bulk-betaald-actie en de losse betaald-actie. Een aan een order gekoppelde
 * factuur met een MATCH_AFWIJKING die NIET expliciet geaccepteerd is, is niet
 * betaalbaar; een lege array betekent "mag betaald worden". Vrije facturen (geen
 * order, dus geen match) en geaccepteerde afwijkingen blokkeren nooit.
 */
export interface BetaalBlokkadeInput {
  /** Matchstatus van de factuur, of null wanneer er geen order/match is (vrije factuur). */
  matchStatus: InkoopMatchStatusValue | null
  /** Of de afwijking expliciet (met reden) geaccepteerd is. */
  matchGeaccepteerd: boolean
  /**
   * batch-66 inkoop-afwijkingen (FO-490) — de open blokkerende afwijkingen uit
   * het register (aantal + bedrag excl. btw in centen). Optioneel: een caller
   * zonder register-context gedraagt zich als voorheen. Een deelbetaling van het
   * onbetwiste deel loopt NIET via deze gate (dat is een eigen actie op
   * /betaald), dus elke blokkade hier betekent: de factuur mag niet als geheel
   * betaald worden.
   */
  openBlokkerendeAfwijkingen?: {
    aantal: number
    bedragCenten: number
    /**
     * Het nummer van de correctiefactuur die deze factuur vervangt (een afwijking
     * is opgelost met de uitkomst Correctiefactuur). Dan mag deze factuur niet
     * meer betaald worden: de correctiefactuur wordt betaald, deze geannuleerd.
     */
    vervangenDoor?: string | null
  } | null
}

export function betaalBlokkades(input: BetaalBlokkadeInput): string[] {
  const blokkades: string[] = []
  if (input.matchStatus === 'MATCH_AFWIJKING' && !input.matchGeaccepteerd) {
    blokkades.push('Afwijking - controleer de 3-way-match voor betaling of accepteer de afwijking met reden.')
  }
  const open = input.openBlokkerendeAfwijkingen
  if (open && open.aantal > 0) {
    blokkades.push(afwijkingBetaalBlokkadeTekst(open.aantal, open.bedragCenten))
  }
  if (open?.vervangenDoor) {
    blokkades.push(afwijkingVervangenBlokkadeTekst(open.vervangenDoor))
  }
  return blokkades
}

// NB: de oude FO-052-helper `kostenToewijzingOverschrijding` (som van hele
// employeeId-regels vs factuurbedrag) is met FO-147 vervangen door
// `toewijzingSomOverschrijdingCenten` in kosten-verdeling.ts — de toewijzing is
// nu een aandeel-bedrag per medewerker per regel, niet meer de hele regel.

// ────────────────────────────────────────────────────────────────────────────
// Code-generators — sequentieel per kalenderjaar
// ────────────────────────────────────────────────────────────────────────────

/**
 * Bouw de volgende PO-code "PO-YYYY-NNN" gegeven het hoogste reeds bestaande
 * nummer voor het kalenderjaar (highestN = 0 als er nog geen records zijn).
 *
 * Sequentieel per kalenderjaar zodat code-collissies onmogelijk zijn binnen
 * één jaar.
 */
export function buildPoNummer(year: number, highestN: number): string {
  const next = highestN + 1
  const padded = String(next).padStart(3, '0')
  return `PO-${year}-${padded}`
}

/**
 * Parsing helper — extracteert het sequentienummer uit een code "PO-2026-007".
 * Returnt 0 als de code niet matched (dan begint de volgende op 1).
 */
export function parsePoNummer(code: string): number {
  const match = code.match(/^PO-\d{4}-(\d+)$/)
  if (!match) return 0
  const n = Number.parseInt(match[1] ?? '0', 10)
  return Number.isFinite(n) ? n : 0
}

/**
 * Bouw de volgende inkoop-factuurnummer "FAC-YYYY-NNNN" (4-cijferig nummer).
 */
export function buildInkoopFactuurNr(year: number, highestN: number): string {
  const next = highestN + 1
  const padded = String(next).padStart(4, '0')
  return `FAC-${year}-${padded}`
}

export function parseInkoopFactuurNr(code: string): number {
  const match = code.match(/^FAC-\d{4}-(\d+)$/)
  if (!match) return 0
  const n = Number.parseInt(match[1] ?? '0', 10)
  return Number.isFinite(n) ? n : 0
}

// ────────────────────────────────────────────────────────────────────────────
// Privé hulpjes
// ────────────────────────────────────────────────────────────────────────────

function roundTwo(n: number): number {
  return Math.round(n * 100) / 100
}
